import {
  defaultUserSettings,
  themeColors,
  themeCss,
  type PublicUser,
  type UserSettings,
} from '@flame/shared';
import { computed, effect, signal } from '@preact/signals';
import { Api, errorMessage, type Page } from './api';

/** undefined while the first /api/auth/state request is in flight. */
export const user = signal<PublicUser | null | undefined>(undefined);
export const needsSetup = signal(false);
export const page = signal<Page | null>(null);
export const settings = computed<UserSettings>(
  () => page.value?.settings ?? defaultUserSettings()
);
export const editMode = signal(false);

export async function loadAuthState() {
  const state = await Api.state();
  needsSetup.value = state.needsSetup;
  user.value = state.user;
}

export async function loadPage() {
  page.value = await Api.page();
}

export function setSettings(next: UserSettings) {
  if (page.value) page.value = { ...page.value, settings: next };
}

export async function signOut() {
  await Api.logout();
  user.value = null;
  page.value = null;
  editMode.value = false;
}

// ---------------------------------------------------------------- toasts

export interface Toast {
  id: number;
  message: string;
  kind: 'info' | 'error';
}
export const toasts = signal<Toast[]>([]);
let toastId = 0;

export function toast(message: string, kind: Toast['kind'] = 'info') {
  const id = ++toastId;
  toasts.value = [...toasts.value, { id, message, kind }];
  setTimeout(() => {
    toasts.value = toasts.value.filter((t) => t.id !== id);
  }, kind === 'error' ? 6000 : 3000);
}

/** Run an action, showing any error as a toast. Returns undefined on failure. */
export async function attempt<T>(fn: () => Promise<T>, success?: string): Promise<T | undefined> {
  try {
    const result = await fn();
    if (success) toast(success);
    return result;
  } catch (err) {
    toast(errorMessage(err), 'error');
    return undefined;
  }
}

// ---------------------------------------------------------------- theming

function headElement<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  id: string
): HTMLElementTagNameMap[K] {
  let el = document.getElementById(id) as HTMLElementTagNameMap[K] | null;
  if (!el) {
    el = document.createElement(tag);
    el.id = id;
    document.head.appendChild(el);
  }
  return el;
}

/**
 * Keep the theme CSS and custom CSS in sync with settings. The server already
 * injected the same CSS into index.html, so this only matters after changes.
 */
effect(() => {
  // Until the signed-in user's settings arrive, keep what the server injected;
  // applying defaults here would flash the wrong theme.
  if (user.value === undefined || (user.value && !page.value)) return;
  const a = settings.value.appearance;
  const css = themeCss(a);
  const vars = headElement('style', 'theme-vars');
  if (vars.textContent !== css) vars.textContent = css;
  const custom = headElement('style', 'custom-css');
  const customCss = user.value ? a.customCss : '';
  if (custom.textContent !== customCss) custom.textContent = customCss;

  const colors = themeColors(a);
  for (const scheme of ['light', 'dark'] as const) {
    const meta = headElement('meta', `theme-color-${scheme}`);
    meta.name = 'theme-color';
    meta.media = `(prefers-color-scheme: ${scheme})`;
    meta.content = colors[scheme];
  }
});

export { errorMessage };
